/**
 * Http status code Mappings
 *
 * Why: Centralized mapping from HTTP codes to error types
 * What: Maps status codes to categories, messages, and codes
 *
 * Benefits:
 *      - Single soucre of truth for HTTP error handling
 *      - Easy to update behavior for specific status codes
 *      - Consistency handling across the application
 */

import { ErrorCategory, ErrorSeverity } from "../types";
import { ERROR_CODES, type ErrorCode } from "./error-codes";
import { ERROR_MESSAGES } from "./error-messages";

/**
 * Http status code metadata
 */

export interface HttpStatusMeta {
  /** Error category */
  category: ErrorCategory;
  /** Error severity */
  severity: ErrorSeverity;
  /** Application error code */
  code: ErrorCode;
  /** User friendly message */
  message: string;
  /** Whether request can be retried */
  isRetryable: boolean;
}

/**
 * Http status code to metadata mapping
 */

export const HTTP_STATUS_MAP: Record<number, HttpStatusMeta> = {
  // 4xx Client Error
  400: {
    category: ErrorCategory.CLIENT,
    severity: ErrorSeverity.LOW,
    code: ERROR_CODES.CLIENT_BAD_REQUEST,
    message: ERROR_MESSAGES.BAD_REQUEST,
    isRetryable: false,
  },
  401: {
    category: ErrorCategory.AUTHENTICATION,
    severity: ErrorSeverity.MEDIUM,
    code: ERROR_CODES.AUTH_UNAUTHORIZED,
    message: ERROR_MESSAGES.UNAUTHORIZED,
    isRetryable: false,
  },
  403: {
    category: ErrorCategory.AUTHENTICATION,
    severity: ErrorSeverity.MEDIUM,
    code: ERROR_CODES.AUTHZ_FORBIDDEN,
    message: ERROR_MESSAGES.FORBIDDEN,
    isRetryable: false,
  },
  404: {
    category: ErrorCategory.NOT_FOUND,
    severity: ErrorSeverity.LOW,
    code: ERROR_CODES.CLIENT_NOT_FOUND,
    message: ERROR_MESSAGES.NOT_FOUND,
    isRetryable: false,
  },
  408: {
    category: ErrorCategory.TIMEOUT,
    severity: ErrorSeverity.MEDIUM,
    code: ERROR_CODES.NETWORK_TIMEOUT,
    message: ERROR_MESSAGES.NETWORK_TIMEOUT,
    isRetryable: true,
  },
  409: {
    category: ErrorCategory.CLIENT,
    severity: ErrorSeverity.LOW,
    code: ERROR_CODES.CLIENT_CONFLICT,
    message: ERROR_MESSAGES.CONFLICT,
    isRetryable: false,
  },
  413: {
    category: ErrorCategory.CLIENT,
    severity: ErrorSeverity.LOW,
    code: ERROR_CODES.CLIENT_PAYLOAD_TOO_LARGE,
    message: ERROR_MESSAGES.PAYLOAD_TOO_LARGE,
    isRetryable: false,
  },
  422: {
    category: ErrorCategory.VALIDATION,
    severity: ErrorSeverity.LOW,
    code: ERROR_CODES.VALIDATION_ERROR,
    message: ERROR_MESSAGES.VALIDATION_ERROR,
    isRetryable: false,
  },
  429: {
    category: ErrorCategory.RATE_LIMIT,
    severity: ErrorSeverity.MEDIUM,
    code: ERROR_CODES.RATE_LIMIT_EXCEEDED,
    message: ERROR_MESSAGES.RATE_LIMIT_EXCEEDED,
    isRetryable: true,
  },
  500: {
    category: ErrorCategory.SERVER,
    severity: ErrorSeverity.HIGH,
    code: ERROR_CODES.SERVER_INTERNAL_ERROR,
    message: ERROR_MESSAGES.SERVER_ERROR,
    isRetryable: true,
  },
  502: {
    category: ErrorCategory.SERVER,
    severity: ErrorSeverity.HIGH,
    code: ERROR_CODES.SERVER_BAD_GATEWAY,
    message: ERROR_MESSAGES.SERVER_ERROR,
    isRetryable: true,
  },
  503: {
    category: ErrorCategory.SERVER,
    severity: ErrorSeverity.HIGH,
    code: ERROR_CODES.SERVER_UNAVAILABLE,
    message: ERROR_MESSAGES.SERVICE_UNAVAILABLE,
    isRetryable: true,
  },
  504: {
    category: ErrorCategory.TIMEOUT,
    severity: ErrorSeverity.HIGH,
    code: ERROR_CODES.SERVER_GATEWAY_TIMEOUT,
    message: ERROR_MESSAGES.NETWORK_TIMEOUT,
    isRetryable: true,
  },
};

/**
 * Get metadata for HTTP status code
 * Returns default metadata for unknown status codes
 */
export const getHttpStatusMeta = (status: number): HttpStatusMeta => {
  if (HTTP_STATUS_MAP[status]) {
    return HTTP_STATUS_MAP[status];
  }

  // Default for 4xx errors
  if (status >= 400 && status < 500) {
    return {
      category: ErrorCategory.CLIENT,
      severity: ErrorSeverity.LOW,
      code: ERROR_CODES.CLIENT_BAD_REQUEST,
      message: ERROR_MESSAGES.BAD_REQUEST,
      isRetryable: false,
    };
  }
  // Default for 5xx errors
  if (status >= 500) {
    return {
      category: ErrorCategory.SERVER,
      severity: ErrorSeverity.HIGH,
      code: ERROR_CODES.SERVER_INTERNAL_ERROR,
      message: ERROR_MESSAGES.SERVER_ERROR,
      isRetryable: true,
    };
  }
  // Unknown status code
  return {
    category: ErrorCategory.UNKNOWN,
    severity: ErrorSeverity.MEDIUM,
    code: ERROR_CODES.UNKNOWN_ERROR,
    message: ERROR_MESSAGES.UNKNOWN_ERROR,
    isRetryable: false,
  };
};

/**
 * Check if status code is retryable
 */
export const isRetryableStatus = (status: number): boolean => {
  return getHttpStatusMeta(status).isRetryable;
};

/**
 * Check if status code is a client error
 */
export const isClientError = (status: number): boolean => {
  return status >= 400 && status < 500;
};

/**
 * Check if status code is server error
 */
export const isServerError = (status: number): boolean => {
  return status >= 500;
};
